import {
    ActionButton,
    CommandBar,
    DetailsList,
    DetailsListLayoutMode,
    DetailsRow,
    FontSizes,
    GroupHeader,
    GroupedList,
    IColumn,
    ICommandBarItemProps,
    IDetailsGroupDividerProps,
    IGroup,
    IGroupHeaderProps,
    IRenderFunction,
    IconButton,
    Modal,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    Stack,
    TextField,
} from "@fluentui/react";
import { BudgetDisplayNames, FinOpsReviewStatus, IFinOpsBudget, IFinOpsBudgetFilters } from "../../models/FinOpsBudgets";
import { CategoryDivision, CategoryDivisionDisplayName } from "../../models/CategoryDivision";
import React, { useEffect, useState } from "react";
import { round, toInteger } from "lodash";
import {
    useGetAllHistoricalBudgetVersion,
    useGetServiceLevelBudget,
    useReviewFinOpsBudgets,
    useReviewFinOpsBudgetsInBatch,
} from "../../hooks/useFinOpsBudget";

import { BudgetLayout } from "./BudgetLayout";
import { CSSProperties } from "react";
import FiltersDropDown from "../common/FiltersBanner/FiltersDropDown/FiltersDropDown";
import { IAppState } from "../../store";
import { IServiceTreeData } from "../../reducers/serviceTreeReducer";
import WorkloadFilter from "../common/FiltersBanner/WorkloadFilter/WorkloadFilter";
import { exportToCsv } from "../../utils/common";
import { getBudgetFiscalYear } from "../../utils/BudgetUtils";
import moment from "moment";
import styles from "./ReviewBudget.less";
import { useBoolean } from "@fluentui/react-hooks";
import { useCallback } from "react";
import { useCategoryFilters } from "../../hooks/useFilters";
import { useSelector } from "react-redux";

const priorityMap: Partial<Record<CategoryDivision, number>> = {
    [CategoryDivision.Owner]: 2,
    [CategoryDivision.ReviewStatus]: 1,
};

function getPriority(key: CategoryDivision): number {
    return priorityMap[key] ?? 0;
}

const SupportedFilters = [
    CategoryDivision.Owner,
    CategoryDivision.ReviewStatus,
    CategoryDivision.Category,
    CategoryDivision.Type,
    CategoryDivision.Submitter,
    CategoryDivision.Service,
    CategoryDivision.GrowthRate,
];

function isSearchableFilter(category: CategoryDivision) {
    switch (category) {
        case CategoryDivision.Owner:
        case CategoryDivision.ReviewStatus:
        case CategoryDivision.Category:
        case CategoryDivision.Type:
        case CategoryDivision.Submitter:
        case CategoryDivision.Service:
            return true;
        default:
            return false;
    }
}

const intlFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
});

const approvedRejectedStyle: CSSProperties = {
    textAlign: "left",
    fontSize: "16px",
};

const groupHeaderStyles = {
    root: {
        fontSize: "18px",
        fontWeight: "bold",
        padding: "10px 0",
    },
    span: {
        fontSize: "18px",
    },
};

const ReviewBudget: React.FC = () => {
    //Global Filters
    const [selectedFilters, setSelectedFilters] = useState<CategoryDivision[]>(SupportedFilters);
    const { filters: globalFilters, clearAllFiltersOnly } = useCategoryFilters();
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
    const CURRENT_BUDGET_FISCAL_YEAR = getBudgetFiscalYear();

    const [filters, setFilters] = useState<IFinOpsBudgetFilters>({});
    const selectedOwners = globalFilters.filters.Owner;
    const currentOwner = selectedOwners && selectedOwners.length > 0 ? selectedOwners[0] : undefined;
    const {
        data: fetchedItems = [],
        error,
        isLoading,
        refetch: reFetchedItems,
        isRefetching,
    } = useGetServiceLevelBudget({ fiscalYear: CURRENT_BUDGET_FISCAL_YEAR, serviceGM: currentOwner });
    const reviewMutation = useReviewFinOpsBudgets();
    const reviewInBatchMutation = useReviewFinOpsBudgetsInBatch();
    const [items, setItems] = useState<IFinOpsBudget[]>([]);
    const [filterItems, setFilterItems] = useState<IFinOpsBudget[]>([]);
    const [showItems, setShowItems] = useState<IFinOpsBudget[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentComment, setCurrentComment] = useState("");
    const [currentItemId, setCurrentItemId] = useState<number | null>(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [currentHistoricalVersionFilters, setCurrentHistoricalVersionFilters] = useState<Partial<IFinOpsBudget> | null>(null);
    const [isDownloadingEnabled, { setFalse: disableDownloading, setTrue: enableDownloading }] = useBoolean(true);
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
    const [budgetGroup, setGroup] = useState<IGroup[]>([]);

    const { data: historyItems = [], refetch: fetchHistoryItems } = useGetAllHistoricalBudgetVersion({
        fiscalYear: CURRENT_BUDGET_FISCAL_YEAR,
        serviceGM: currentOwner,
    });
    const serviceTree = useSelector<IAppState, IServiceTreeData>((state) => state.serviceTree);

    const columns: IColumn[] = [
        { key: "serviceName", name: "ServiceName", fieldName: "serviceName", minWidth: 20, maxWidth: 200, isResizable: false, className: styles.PrimaryColumn },
        { key: "category", name: "Category", fieldName: "category", minWidth: 20, maxWidth: 200, isResizable: false, className: styles.PrimaryColumn },
        { key: "type", name: "Type", fieldName: "type", minWidth: 20, maxWidth: 200, isResizable: false, className: styles.PrimaryColumn },
        { key: "unit", name: "Unit", fieldName: "unit", minWidth: 20, maxWidth: 200, isResizable: false, className: styles.PrimaryColumn },
        {
            key: "isServiceAffectedByCopilot",
            name: "Is Service Affected By Copilot",
            fieldName: "isServiceAffectedByCopilot",
            minWidth: 20,
            maxWidth: 200,
            isResizable: false,
            className: styles.PrimaryColumn,
            onRender: (item: IFinOpsBudget) => (item.isServiceAffectedByCopilot === 1 ? "True" : "False"),
        },
        {
            key: "increaseRate",
            name: "IncreaseRate",
            fieldName: "increaseRate",
            minWidth: 25,
            maxWidth: 200,
            isResizable: false,
            className: styles.NormalColumn,
            onRender: (item: IFinOpsBudget) => round(item.increaseRate ?? 0, 2) + "%",
        },
        {
            key: "costBudget",
            name: "CostBudget",
            fieldName: "costBudget",
            minWidth: 25,
            maxWidth: 200,
            isResizable: false,
            className: styles.NormalColumn,
            onRender: (item: IFinOpsBudget) => intlFormatter.format(toInteger(item.costBudget)),
        },
        {
            key: "quantityBudget",
            name: "QuantityBudget",
            fieldName: "quantityBudget",
            minWidth: 25,
            maxWidth: 200,
            isResizable: false,
            className: styles.NormalColumn,
            onRender: (item: IFinOpsBudget) => toInteger(item.quantityBudget),
        },
        { key: "submitter", name: "Submitter", fieldName: "submitter", minWidth: 25, maxWidth: 200, isResizable: false, className: styles.NormalColumn },
        {
            key: "justification",
            name: "Justification",
            fieldName: "justification",
            minWidth: 15,
            maxWidth: 200,
            isResizable: true,
            className: styles.NormalColumn,
        },
        {
            key: "history",
            name: "View History Version",
            fieldName: "history",
            minWidth: 200,
            maxWidth: 300,
            isResizable: false,
            onRender: (item: IFinOpsBudget) => <PrimaryButton text="View History Version" onClick={() => openHistoryModal(item)} />,
            className: styles.NormalColumn,
        },
        {
            key: "comment",
            name: "Comment",
            fieldName: "comment",
            minWidth: 200,
            maxWidth: 300,
            isResizable: false,
            onRender: (item: IFinOpsBudget) => <TextField value={item.comment} onClick={() => openCommentModal(item.id, item.comment || "")} readOnly />,
            className: styles.NormalColumn,
        },
        {
            key: "review",
            name: "Approve/Reject",
            minWidth: 200,
            maxWidth: 300,
            isResizable: false,
            onRender: (item: IFinOpsBudget) => {
                if (item.approved === 0) {
                    return (
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton text="Approve" onClick={() => handleApprove(item, FinOpsReviewStatus.Approved)} />
                            <PrimaryButton text="Reject" onClick={() => handleReject(item, FinOpsReviewStatus.Denied)} />
                        </Stack>
                    );
                } else if (item.approved === FinOpsReviewStatus.Approved) {
                    return <div style={approvedRejectedStyle}>Approved</div>;
                } else if (item.approved === FinOpsReviewStatus.Denied) {
                    return <div style={approvedRejectedStyle}>Rejected</div>;
                }
                return null;
            },
            className: styles.NormalColumn,
        },
    ];

    const historyColumns: IColumn[] = [
        { key: "fiscalYear", name: "FiscalYear", fieldName: "fiscalYear", minWidth: 100, maxWidth: 200, isResizable: true },
        { key: "serviceId", name: "ServiceId", fieldName: "serviceId", minWidth: 100, maxWidth: 200, isResizable: true },
        { key: "category", name: "Category", fieldName: "category", minWidth: 20, maxWidth: 200, isResizable: false },
        { key: "type", name: "Type", fieldName: "type", minWidth: 20, maxWidth: 200, isResizable: false },
        {
            key: "isServiceAffectedByCopilot",
            name: "Is Service Affected By Copilot",
            fieldName: "isServiceAffectedByCopilot",
            minWidth: 20,
            maxWidth: 200,
            isResizable: false,
            onRender: (item: IFinOpsBudget) => (item.isServiceAffectedByCopilot === 1 ? "True" : "False"),
        },
        {
            key: "increaseRate",
            name: "IncreaseRate",
            fieldName: "increaseRate",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IFinOpsBudget) => item.increaseRate + "%",
        },
        {
            key: "costBudget",
            name: "CostBudget",
            fieldName: "costBudget",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IFinOpsBudget) => intlFormatter.format(item.costBudget),
        },
        { key: "quantityBudget", name: "QuantityBudget", fieldName: "quantityBudget", minWidth: 100, maxWidth: 200, isResizable: true },
        { key: "submitter", name: "Submitter", fieldName: "submitter", minWidth: 100, maxWidth: 200, isResizable: true },
        {
            key: "version",
            name: "Version",
            fieldName: "version",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IFinOpsBudget) => moment(item.version).format("YYYY-MM-DD HH:mm:ss"),
        },
        { key: "approver", name: "Approver", fieldName: "approver", minWidth: 100, maxWidth: 200, isResizable: true },
        {
            key: "approved",
            name: "Approved",
            fieldName: "approved",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IFinOpsBudget) => (item.approved === 2 ? "Approved" : "Rejected"),
        },
        {
            key: "approvedTime",
            name: "ApprovedTime",
            fieldName: "approvedTime",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: IFinOpsBudget) => moment(item.approvedTime).format("YYYY-MM-DD HH:mm:ss"),
        },
        { key: "justification", name: "Justification", fieldName: "justification", minWidth: 100, maxWidth: 200, isResizable: true },
        { key: "comment", name: "Comment", fieldName: "comment", minWidth: 100, maxWidth: 200, isResizable: true },
    ];

    const generateGroups = (items: IFinOpsBudget[]): IGroup[] => {
        const groups: IGroup[] = [];
        const groupedItems = items.reduce((acc, item) => {
            const serviceName = serviceTree.indexMap.get(item.serviceId)?.n || "Unknown";
            if (!acc[serviceName]) {
                acc[serviceName] = [];
            }
            acc[serviceName].push(item);
            return acc;
        }, {} as Record<string, IFinOpsBudget[]>);
        let startIndex = 0;
        for (const serviceName in groupedItems) {
            groups.push({
                key: serviceName,
                name: serviceName,
                startIndex,
                count: groupedItems[serviceName].length,
                isCollapsed: true,
            });
            startIndex += groupedItems[serviceName].length;
        }
        return groups;
    };

    const handleApprove = async (item: IFinOpsBudget, ReviewRes: number) => {
        try {
            item.approved = ReviewRes;
            await reviewMutation.mutateAsync({ ...item });
            alert("Approval successful!");
            setFilterItems(filterItems.map((i) => (i.id === item.id ? { ...i, approved: ReviewRes } : i)));
        } catch (error) {
            console.error("Error approving budget:", error);
            alert("Error approving budget: " + error);
        }
    };

    const handleReject = async (item: IFinOpsBudget, ReviewRes: number) => {
        if (!currentComment) {
            // Check if a comment has been provided
            openCommentModal(item.id, ""); // Open the comment modal if no comment is provided
            alert("Please provide a comment before rejecting.");
            return; // Exit the function to wait for the comment
        }

        try {
            item.approved = ReviewRes;
            await reviewMutation.mutateAsync({ ...item });
            alert("Rejection successful!");
            setFilterItems(filterItems.map((i) => (i.id === item.id ? { ...i, approved: ReviewRes } : i)));
        } catch (error) {
            console.error("Error rejecting budget:", error);
            alert("Error rejecting budget: " + error);
        }
    };

    const openCommentModal = (id: number, comment: string) => {
        setCurrentComment(comment);
        setCurrentItemId(id);
        setIsModalOpen(true);
    };

    const closeCommentModal = () => {
        setIsModalOpen(false);
        if (currentItemId !== null) {
            const updatedItems = items.map((item) => (item.id === currentItemId ? { ...item, comment: currentComment } : item));
            setItems(updatedItems);
        }
    };

    const openHistoryModal = (item: IFinOpsBudget) => {
        fetchHistoryItems();
        setCurrentHistoricalVersionFilters({
            fiscalYear: item.fiscalYear,
            serviceId: item.serviceId,
            category: item.category,
            type: item.type,
            submitter: item.submitter,
        });
        setIsHistoryModalOpen(true);
    };

    const closeHistoryModal = () => {
        setIsHistoryModalOpen(false);
        setCurrentHistoricalVersionFilters(null);
    };

    const filteredHistoryItems = historyItems.filter(
        (historyItem) =>
            historyItem.fiscalYear === currentHistoricalVersionFilters?.fiscalYear &&
            historyItem.serviceId === currentHistoricalVersionFilters?.serviceId &&
            historyItem.category === currentHistoricalVersionFilters?.category &&
            historyItem.type === currentHistoricalVersionFilters?.type &&
            historyItem.submitter === currentHistoricalVersionFilters?.submitter &&
            historyItem.approved !== null &&
            historyItem.approved !== 0
    );

    function getFilterList(category: CategoryDivision) {
        switch (category) {
            case CategoryDivision.Owner:
                const owners = new Set<string>();
                serviceTree.indexMap.forEach((serviceTreeItem) => {
                    const serviceOwners = serviceTreeItem.o.split(";") || ([] as string[]);
                    if (Array.isArray(serviceOwners)) {
                        serviceOwners.filter((item) => item && item.length > 0).forEach((owner) => owners.add(owner));
                    }
                });
                return Array.from(owners).sort();
            case CategoryDivision.ReviewStatus:
                return ["Approved", "Rejected", "Pending"];
            case CategoryDivision.Category:
                const categories = new Set<string>();
                filterItems.forEach((item) => {
                    if (item.category) {
                        categories.add(item.category);
                    }
                });
                return Array.from(categories).sort();
            case CategoryDivision.Type:
                const types = new Set<string>();
                filterItems.forEach((item) => {
                    if (item.type) {
                        types.add(item.type);
                    }
                });
                return Array.from(types).sort();
            case CategoryDivision.Submitter:
                const submitters = new Set<string>();
                filterItems.forEach((item) => {
                    if (item.submitter) {
                        submitters.add(item.submitter);
                    }
                });
                return Array.from(submitters).sort();
            case CategoryDivision.Service:
                const services = new Set<string>();
                filterItems.forEach((item) => {
                    if (item.serviceId) {
                        const serviceName = serviceTree.indexMap.get(item.serviceId)?.n || "Unknown";
                        services.add(serviceName);
                    }
                });
                return Array.from(services).sort();
            case CategoryDivision.GrowthRate:
                return ["<= 0%", "> 0%", "> 4%", "> 8%", "> 12%", "> 15%", "> 20%", "> 30%", "> 40%", "> 50%", "> 60%", "> 100%"];
            default:
                return [];
        }
    }

    const selectedfilterItems = selectedFilters.map((filter) => ({
        key: `${filter}Filter`, // unique key
        onRender: () => (
            <div className={styles.Filter}>
                <WorkloadFilter
                    hidden={!selectedFilters.includes(filter)}
                    category={filter}
                    optionList={getFilterList(filter)}
                    displayName={CategoryDivisionDisplayName[filter]}
                    isSearchable={isSearchableFilter(filter)}
                    isSingleSelect={filter === CategoryDivision.Owner || filter === CategoryDivision.GrowthRate ? true : false}
                />
            </div>
        ),
    }));

    //Clear all Global Filters
    const onClearAll = useCallback(() => {
        clearAllFiltersOnly();
    }, [clearAllFiltersOnly]);

    const filterBarItem: ICommandBarItemProps[] = [
        {
            key: "FilterManage",
            onRender: () => (
                <div className={styles.Filter}>
                    <FiltersDropDown
                        selectedKeys={selectedFilters}
                        onChange={(newSelectedFilters: CategoryDivision[]) => {
                            const sortedFilters = newSelectedFilters.sort((a: CategoryDivision, b: CategoryDivision) => {
                                //order first
                                return getPriority(a) < getPriority(b) ? 1 : -1;
                            });

                            setSelectedFilters(sortedFilters); //set oredered filter
                        }}
                        supportedFilters={SupportedFilters}
                    />
                    <ActionButton key="clear" iconProps={{ iconName: "ClearFilter" }} onClick={onClearAll} style={{ padding: 0 }}>
                        Reset all
                    </ActionButton>
                </div>
            ),
        },
        ...selectedfilterItems,
    ];

    //Get all used CategoryDivision
    function getSelectedFiltersFromGlobalFilter() {
        const filters = globalFilters.filters;
        const tempSelectedFilters = new Set<CategoryDivision>([]);
        selectedFilters.forEach((item) => {
            // Insert current filter first
            tempSelectedFilters.add(item);
        });

        SupportedFilters.forEach((filterKey) => {
            // Add global filter then
            if ((filters[filterKey]?.length || 0) > 0) {
                tempSelectedFilters.add(filterKey);
            }
        });

        return tempSelectedFilters;
    }

    function filterByGrowthRate(growthRateFilter: string[], growthRate: number) {
        if (growthRateFilter && growthRateFilter.length > 0) {
            return growthRateFilter.every((growthRateRule) => {
                const operator = growthRateRule.startsWith(">") ? ">" : "<=";
                const value = parseFloat(growthRateRule.replace(/[^\d.]/g, "")); // Extract numeric value

                if (operator === ">" && growthRate > value) {
                    return true;
                } else if (operator === "<=" && growthRate <= value) {
                    return true;
                }
                return false; // If any rule does not match, return false
            });
        }
        return true; // If no filters are provided, return true
    }

    /***** All Status Change ********************************/
    useEffect(() => {
        if (isLoading || isRefetching) {
            reFetchedItems();
            setItems([]);
            setFilterItems([]);
            return;
        }
        if (!fetchedItems) return;
        const allChildren = fetchedItems.flatMap((item) => item.children || []);
        setItems(allChildren);
        setFilterItems(allChildren);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isRefetching]);

    useEffect(() => {
        const filters = globalFilters.filters;
        const reviewStatusList = filters.ReviewStatus;
        const categoryList = filters.Category;
        const typeList = filters.Type;
        const submitterList = filters.Submitter;
        const growthRate = filters.GrowthRate;
        const serviceList = filters.Service;

        const tmpItems = [...items];
        const groupedByServiceId = tmpItems.reduce((acc, item) => {
            if (!acc[item.serviceId]) {
            acc[item.serviceId] = [];
            }
            acc[item.serviceId].push(item);
            return acc;
        }, {} as Record<string, IFinOpsBudget[]>);
        const serviceIncreaseRate: Record<string, number> = {};
        Object.keys(groupedByServiceId).forEach((serviceId) => {
            const groupItems = groupedByServiceId[serviceId];
            const totalHistoricalCost = groupItems.reduce((sum, item) => sum + (item.lastYearCost || 0), 0);
            const totalBudget = groupItems.reduce((sum, item) => sum + (item.costBudget || 0), 0);
            serviceIncreaseRate[serviceId] = totalHistoricalCost > 0
                ? round((totalBudget / totalHistoricalCost - 1) * 100, 2)
                : 0;
        });
        

        const tmpFilterItems = tmpItems.filter((item) => {
            const reviewStatusFilter = reviewStatusList?.length
                ? reviewStatusList.includes(item.approved === 2 ? "Approved" : item.approved === 1 ? "Rejected" : "Pending")
                : true;
            const categoryFilter = categoryList?.length ? categoryList.includes(item.category) : true;
            const typeFilter = typeList?.length ? typeList.includes(item.type) : true;
            const submitterFilter = submitterList?.length ? submitterList.includes(item.submitter || "") : true;
            const serviceFilter = serviceList?.length ? serviceList.includes(item.serviceId) : true;
            const growthRateFilter = growthRate?.length ? filterByGrowthRate(growthRate, serviceIncreaseRate[item.serviceId]) : true;
            return reviewStatusFilter && categoryFilter && typeFilter && submitterFilter && serviceFilter && growthRateFilter;
        });

        setSelectedFilters([...getSelectedFiltersFromGlobalFilter()]);
        setFilterItems(tmpFilterItems);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalFilters.filters, items]);

    useEffect(() => {
        const groups = generateGroups(filterItems);
        setGroup(groups);
        setShowItems(filterItems);
    }, [filterItems]);

    const _selection = new Selection({
        onSelectionChanged: () => {
            setSelectedIndices(_selection.getSelectedIndices());
        },
    });

    const handleApproveInBatch = async () => {
        const selectedItems = selectedIndices.map((index) => showItems[index]);
        try {
            await reviewInBatchMutation.mutateAsync({data: selectedItems.map((item) => item.id), reviewStatus: FinOpsReviewStatus.Approved});
            setFilterItems(
                filterItems.map((i) => (selectedItems.some((selected) => selected.id === i.id) ? { ...i, approved: FinOpsReviewStatus.Approved } : i))
            );
            alert("Approval successful!");
        } catch (error) {
            console.error("Error approving budget:", error);
            alert("Error approving budget: " + error);
        }
    };

    const handleRejectInBatch = async () => {
        const selectedItems = selectedIndices.map((index) => showItems[index]);
        try {
            await reviewInBatchMutation.mutateAsync({data: selectedItems.map((item) => item.id), reviewStatus: FinOpsReviewStatus.Denied});
            setFilterItems(
                filterItems.map((i) => (selectedItems.some((selected) => selected.id === i.id) ? { ...i, approved: FinOpsReviewStatus.Denied } : i))
            );
            alert("Reject successful!");
        } catch (error) {
            console.error("Error rejecting budget:", error);
            alert("Error rejecting budget: " + error);
        }
    };

    const onRenderHeader: IRenderFunction<IGroupHeaderProps> = (props) => {
        if (!props) return null;

        return (
            <div>
                <GroupHeader
                    {...props}
                    onGroupHeaderClick={undefined}
                    styles={{
                        title: {
                            cursor: "auto",
                        },
                    }}
                    onRenderTitle={renderTitle}
                />
            </div>
        );
    };

    const renderTitle: IRenderFunction<IGroupHeaderProps> = (props) => {
        if (!props) return null;

        const { data } = props.group || {};
        const ServiceName = data?.name || "";
        const IsServiceAffectedByCopilot = data?.summarizedValues.isServiceAffectedByCopilot;
        const totalIncreaseRate = data?.summarizedValues.increaseRate;
        const totalCostBudget = data?.summarizedValues.costBudget;
        const count = props.group?.count || 0;
        const groupTitle = "ServiceName";

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "auto",
                    whiteSpace: "nowrap",
                    width: "90%",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        width: "50%",
                        textAlign: "left",
                        paddingLeft: 10,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                    }}
                >
                    <div style={{ fontWeight: 700, paddingRight: 5 }}>{groupTitle}:</div>
                    {ServiceName}({count})
                </div>
                <div style={{ display: "flex", width: "20%", textAlign: "right" }}>
                    <div style={{ fontWeight: 700, paddingRight: 5 }}>IsServiceAffectedByCopilot:</div>
                    {IsServiceAffectedByCopilot}
                </div>
                <div style={{ display: "flex", width: "15%", textAlign: "right" }}>
                    <div style={{ fontWeight: 700, paddingRight: 5 }}>IncreaseRate:</div>
                    {totalIncreaseRate}
                </div>
                <div style={{ display: "flex", width: "15%", textAlign: "right" }}>
                    <div style={{ fontWeight: 700, paddingRight: 5 }}>CostBudget:</div>
                    {totalCostBudget}
                </div>
            </div>
        );
    };

    // interface ICustomGroupRowProps extends IDetailsGroupDividerProps {
    //     items: IFinOpsBudget[];
    //   }

    // function CustomGroupRow(props: ICustomGroupRowProps) {
    //     return (
    //       <div style={{ position: 'relative' }}>
    //         <GroupHeader
    //           {...props}
    //           indentWidth={0}
    //           styles={{
    //             headerCount: { display: 'none' },
    //             root: {
    //               position: 'absolute',
    //               background: 'transparent',
    //               height: 50,
    //               zIndex: 4, // Adjust zIndex for proper layering
    //               left: 15 * (props.groupLevel ?? 0),
    //               ':hover': {
    //                 backgroundColor: '#f3f2f1', // Add hover color
    //               },
    //             },
    //           }}
    //         />
    //         <DetailsRow
    //           {...props}
    //           styles={{
    //             root: {
    //               height: 50,
    //               zIndex: 3, // Adjust zIndex for proper layering
    //               pointerEvents: 'none', // Ensure DetailsRow is not blocked by GroupHeader
    //             },
    //           }}
    //           itemIndex={-1}
    //           item={['isServiceAffectedByCopilot', 'increaseRate', 'costBudget'].reduce(
    //             (acc, column) => ({
    //               ...acc,
    //               [column]: props.items
    //                 .slice(
    //                   props.group?.startIndex ?? 0,
    //                   (props.group?.startIndex ?? 0) + (props.group?.count ?? 0)
    //                 )
    //                 .reduce((sum, item) => sum + (item[column as keyof IFinOpsBudget] as number), 0),
    //             }),
    //             { thumbnail: '' }
    //           )}
    //         />
    //       </div>
    //     );
    //   }

    const handleDownload = useCallback(() => {
        disableDownloading();
        exportToCsv(
            items.map((item) => ({
                ...item,
                isServiceAffectedByCopilot: item.isServiceAffectedByCopilot ? "True" : "False",
                serviceId: serviceTree.indexMap.get(item.serviceId)?.n || "Unknown",
            })),
            "PendingBudgetAsks",
            [
                "serviceId",
                "category",
                "type",
                "unit",
                "isServiceAffectedByCopilot",
                "increaseRate",
                "costBudget",
                "quantityBudget",
                "submitter",
                "justification",
            ],
            BudgetDisplayNames
        );
        enableDownloading();
    }, [disableDownloading, enableDownloading, items]);

    if (error) {
        return <div>Error fetching pending review budget asks: {error.message}</div>;
    }

    const toggleGroup = (groupKey: string) => {
        setExpandedGroup((prevState) => (prevState === groupKey ? null : groupKey));
    };

    return (
        <BudgetLayout>
            <div className="container">
                <div className={styles.header}>
                    <h1>Review Budget</h1>
                </div>
                <Separator styles={{ root: styles.Separator }} />
                <CommandBar
                    styles={{
                        root: {
                            height: "32px",
                        },
                    }}
                    key={"CommandBar_filter"}
                    items={filterBarItem}
                />
                {budgetGroup.length > 0 && (
                    <ActionButton
                        onClick={handleDownload}
                        iconProps={{ iconName: "Download" }}
                        text={isDownloadingEnabled ? "Download All" : "Downloading"}
                        disabled={!isDownloadingEnabled}
                    />
                )}
                {selectedIndices.length > 0 && <><ActionButton onClick={handleApproveInBatch} iconProps={{ iconName: "Accept" }} text={"Approve Selected"} /><ActionButton onClick={handleRejectInBatch} iconProps={{ iconName: "Cancel" }} text={"Reject Selected"} /></>}
                {isLoading ? (
                    <Spinner label="Loading..." />
                ) : (
                    <DetailsList
                        items={showItems}
                        columns={columns}
                        groups={budgetGroup.map((group) => {
                            const groupItems = showItems.slice(group.startIndex, group.startIndex + group.count);
                            const summarizedValues = columns.reduce((acc, column) => {
                                if (column.key === "costBudget") {
                                    acc[column.key] = intlFormatter.format(
                                        groupItems.reduce((sum, item) => sum + (item.costBudget || 0), 0)
                                    );
                                } else if (column.key === "increaseRate") {
                                    const totalHistoricalCost = groupItems.reduce((sum, item) => sum + (item.lastYearCost || 0), 0);
                                    const totalBudget = groupItems.reduce((sum, item) => sum + (item.costBudget || 0), 0);
                                    acc[column.key] = totalHistoricalCost > 0
                                        ? round((totalBudget / totalHistoricalCost - 1) * 100, 1) + "%"
                                        : "0%";
                                } else if (column.key === "isServiceAffectedByCopilot") {
                                    acc[column.key] = groupItems.some((item) => item.isServiceAffectedByCopilot === 1) ? "True" : "False";
                                }
                                 else {
                                    acc[column.key] = ""; // Default empty for other columns
                                }
                                return acc;
                            }, {} as Record<string, string | number>);

                            group.data = { name: group.name, summarizedValues };
                            return group;
                        })}
                        groupProps={{
                            onRenderHeader
                          }}
                        setKey="set"
                        selection={_selection}
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        selectionMode={SelectionMode.multiple}
                        ariaLabelForSelectionColumn="Toggle selection"
                        checkButtonAriaLabel="select row"
                    />
                )}
                <Modal isOpen={isModalOpen} onDismiss={closeCommentModal} isBlocking={false} containerClassName={styles.modal}>
                    <div>
                        <div className={styles.modalHeader}>
                            <h2>Add Comment</h2>
                        </div>
                        <TextField
                            label="Comment"
                            value={currentComment}
                            onChange={(e, newValue) => setCurrentComment(newValue || "")}
                            className={styles.popUpWindow}
                        />
                        <div className={styles.modalFooter}>
                            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
                                <PrimaryButton text="Save" onClick={closeCommentModal} className={styles.button} />
                                <PrimaryButton text="Cancel" onClick={() => setIsModalOpen(false)} className={styles.button} />
                            </Stack>
                        </div>
                    </div>
                </Modal>
                <Modal isOpen={isHistoryModalOpen} onDismiss={closeHistoryModal} isBlocking={false} containerClassName={styles.modal}>
                    <div>
                        <div className={styles.modalHeader}>
                            <h2>Historical Budget Versions</h2>
                        </div>
                        <DetailsList items={filteredHistoryItems} columns={historyColumns} setKey="set" layoutMode={DetailsListLayoutMode.fixedColumns} />
                        <div className="modalFooter">
                            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
                                <PrimaryButton text="Close" onClick={closeHistoryModal} className={styles.button} />
                            </Stack>
                        </div>
                    </div>
                </Modal>
            </div>
        </BudgetLayout>
    );
};

export default ReviewBudget;
